import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Link, Navigate } from 'react-router-dom';
import { backendUrl } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { GAME_MODE_DEFS } from '../GameModes/ModeSelector';
import PasswordField from '../UI/PasswordField';

const BACKEND = backendUrl;

const C = {
    bg:     '#060b1f',
    card:   'rgba(13,19,50,0.65)',
    border: 'rgba(255,255,255,0.08)',
    cyan:   '#00d4ff',
    orange: '#ff6b00',
    green:  '#00c853',
    amber:  '#ffb300',
    red:    '#ff3b30',
};

const METHOD_COLOR = { GET: '#00c853', POST: '#ff6b00', PUT: '#00d4ff', DELETE: '#ff3b30' };

const PLAYER_COLORS = ['#ef4444','#3b82f6','#22c55e','#eab308']; // red, blue, green, yellow
const PLAYER_LABELS = ['Player 1','Player 2','Player 3','Player 4'];

// ── Bot setup combinations ─────────────────────────────────────────────────────
const BOT_SETUP_MAP = {
    'none':    null,
    '2p_1h1b': { vsBot: 1, players: 2, humans: 1, label: '2 Players — 1 Human + 1 Bot' },
    '3p_2h1b': { vsBot: 1, players: 3, humans: 2, label: '3 Players — 2 Humans + 1 Bot' },
    '3p_1h2b': { vsBot: 2, players: 3, humans: 1, label: '3 Players — 1 Human + 2 Bots' },
    '4p_3h1b': { vsBot: 1, players: 4, humans: 3, label: '4 Players — 3 Humans + 1 Bot' },
    '4p_2h2b': { vsBot: 2, players: 4, humans: 2, label: '4 Players — 2 Humans + 2 Bots' },
    '4p_1h3b': { vsBot: 3, players: 4, humans: 1, label: '4 Players — 1 Human + 3 Bots' },
};

// ── per-player fields definition (only for create-game) ───────────────────────
const PER_PLAYER_FIELDS = [
    { key: 'name',    label: 'Name',                   type: 'text', placeholder: 'e.g. Rahul' },
    { key: 'mobile',  label: 'Mobile',                 type: 'text', placeholder: '9876543210' },
    { key: 'email',   label: 'Email',                  type: 'text', placeholder: 'player@email.com' },
    { key: 'ip',      label: 'IP Lock (optional)',      type: 'text', placeholder: '192.168.1.1' },
];

const emptyPlayer = () => ({ name: '', mobile: '', email: '', ip: '' });

// ── endpoint definitions ──────────────────────────────────────────────────────
const ENDPOINTS = [
    {
        id: 'create-game',
        group: 'Game API',
        name: 'Create Game Room',
        method: 'POST',
        path: '/api/create-game',
        description: 'Create a Ludo game room with player-wise JSON objects for each human player. Each player name is required; mobile, email, and IP lock are optional.',
        auth: false,
        hasPerPlayer: true,
        bodyFields: [
            { key: 'apikey',   label: 'API Key',      type: 'text',   required: true,  placeholder: 'your-api-key' },
            { key: 'password', label: 'Room Password', type: 'password', required: false, placeholder: 'leave blank for public room' },
            { key: '_botSetup', label: 'Bot Setup 🤖', type: 'select', required: false,
              options: ['none','2p_1h1b','3p_2h1b','3p_1h2b','4p_3h1b','4p_2h2b','4p_1h3b'],
              optionLabels: {
                  'none':    '🧑‍🤝‍🧑 No Bot — Multiplayer (humans only)',
                  '2p_1h1b': '2 Players — 1 Human + 1 Bot',
                  '3p_2h1b': '3 Players — 2 Humans + 1 Bot',
                  '3p_1h2b': '3 Players — 1 Human + 2 Bots',
                  '4p_3h1b': '4 Players — 3 Humans + 1 Bot',
                  '4p_2h2b': '4 Players — 2 Humans + 2 Bots',
                  '4p_1h3b': '4 Players — 1 Human + 3 Bots',
              },
              default: 'none',
            },
            { key: 'botWinRate', label: 'Bot Win Rate % 🎯', type: 'select', required: false,
              options: ['10','20','30','40','50','60','70','80','90','100'],
              optionLabels: { '10': '10% — Very Easy', '20': '20% — Easy', '30': '30%', '40': '40%', '50': '50% — Balanced', '60': '60%', '70': '70%', '80': '80% — Hard', '90': '90% — Very Hard (default)', '100': '100% — Impossible' },
              default: '90', toNumber: true,
              showIf: vals => vals._botSetup && vals._botSetup !== 'none',
              note: 'Bot uses smart move logic. 90% = wins most turns.',
            },
            { key: 'players', label: 'Max Players', type: 'select', required: false,
              options: ['2','3','4'], default: '4', isPlayerCount: true,
              showIf: vals => !vals._botSetup || vals._botSetup === 'none',
            },
            { key: 'isEncrypted', label: 'Encrypted Session 🔐', type: 'select', required: false,
              options: ['true'], default: 'true',
              optionLabels: { 'true': 'true — always generate unique cyber-key per player (auto-saved to localStorage)' },
              note: 'Key is 20–30 chars, mixed-case alphanumeric, cryptographically random. Stored in DB and sent over socket after join. It is not placed in the URL.',
            },
            { key: 'viewroom', label: 'View Room Link 👁', type: 'select', required: false,
              options: ['true','false'], default: 'true',
              optionLabels: { 'true': 'true — include spectator watch URL in response', 'false': 'false — hide watch URL from response' },
            },
            (() => {
              // Build gameMode field dynamically from GAME_MODE_DEFS so labels stay in sync
              const ORDER = ['classic','popular','quick','reverse','token_1','token_2','token_3','no_cut','team_2v2'];
              const defMap = Object.fromEntries(GAME_MODE_DEFS.map(d => [d.value, d]));
              const optionLabels = {};
              const descriptions = {};
              ORDER.forEach(v => {
                const d = defMap[v];
                if (d) {
                  optionLabels[v] = `${d.emoji} ${d.label}${d.popular ? ' ★' : ''}`;
                  descriptions[v]  = `${d.emoji} ${d.label} — ${d.en}`;
                }
              });
              return { key: 'gameMode', label: 'Game Mode 🎮', type: 'select', required: false,
                options: ORDER, optionLabels, descriptions, default: 'classic' };
            })(),
            { key: 'voiceEnabled', label: 'Voice Chat 🎙️',       type: 'select', required: false, options: ['true','false'],  default: 'true'  },
            { key: 'chatEnabled',  label: 'Text Chat 💬',         type: 'select', required: false, options: ['true','false'],  default: 'true'  },
            { key: 'emojiEnabled', label: 'Emoji Reactions 😄',   type: 'select', required: false, options: ['true','false'],  default: 'true'  },
            { key: 'openPawnValues', label: 'Open Pawn Dice 🎲', type: 'text', required: false, placeholder: '1,6',
              note: 'Comma-separated dice values allowed to open a pawn from base. Example: `1,6` or `6`.',
            },
            { key: 'redirectUrl',  label: 'Redirect URL 🔗',      type: 'text',   required: false, placeholder: 'https://yoursite.com/result',
              note: 'Optional. After the game ends, players see a "Go to Website" button and auto-redirect after 60 seconds.',
            },
            { key: 'callbackUrl',  label: 'Callback URL (Webhook) 📡', type: 'text', required: false, placeholder: 'https://yourserver.com/webhook',
              note: 'Optional. Your server receives POST webhooks for: game_created, player_joined, game_started, pawn_home, game_over.',
            },
        ],
        urlParams: [],
        queryParams: [],
        response: {
            status:       'success',
            roomId:       'ROOM_ID',
            gameMode:     'popular',
            maxPlayers:   2,
            humanCount:   2,
            voiceEnabled: true,
            chatEnabled:  true,
            emojiEnabled: true,
            openPawnValues: [1, 6],
            isEncrypted:  true,
            vsBot:        false,
            password:     '',
            players: [
                { player: 1, name: 'Rahul', join_url: `${window.location.origin}/join/ROOM_ID?token=abc123`, token: 'abc123', encKey: 'KpT7xmQ2nR9bL4vF8wZ' },
                { player: 2, name: 'Priya', join_url: `${window.location.origin}/join/ROOM_ID?token=def456`, token: 'def456', encKey: 'NrX3yJhB6qWmSvD9kPz' },
            ],
            room_url:     `${window.location.origin}/join/ROOM_ID`,
            view_url:     `${window.location.origin}/watch/ROOM_ID`,
            pricing: {
                currency: 'credits',
                basePrice: 10,
                fallbackBasePrice: 10,
                perPlayerPrice: 0,
                humanCount: 2,
                totalPrice: 10,
                priceSource: 'fallback',
                priceRole: 'api_manager',
            },
            redirectUrl:  'https://yoursite.com/result (if provided)',
            callbackUrl:  'https://yourserver.com/webhook (if provided)',
        },
    },
    {
        id: 'available-games',
        group: 'Game API',
        name: 'List Available Game Modes',
        method: 'GET',
        path: '/api/available-games',
        description: 'Returns all game modes currently available for room creation for the logged-in account, including descriptions, player limits, bot support, and default create-room settings.',
        auth: true,
        bodyFields: [],
        urlParams: [],
        queryParams: [],
        response: {
            status: 'success',
            count: 9,
            games: [
                {
                    gameMode: 'classic',
                    label: 'Classic',
                    description: 'Standard Ludo. All 4 tokens must reach home to win.',
                    minPlayers: 2,
                    maxPlayers: 4,
                    recommendedPlayers: [2, 3, 4],
                    supportsBots: true,
                    defaults: {
                        isEncrypted: true,
                        viewroom: true,
                    },
                },
                {
                    gameMode: 'team_2v2',
                    label: 'Team 2v2',
                    description: 'Four-player team mode. Team to finish all tokens wins.',
                    minPlayers: 4,
                    maxPlayers: 4,
                    recommendedPlayers: [4],
                    supportsBots: false,
                    defaults: {
                        isEncrypted: true,
                        viewroom: true,
                    },
                },
            ],
        },
    },
    {
        id: 'list-games',
        group: 'Game API',
        name: 'List API Games',
        method: 'GET',
        path: '/api/games',
        description: 'Returns structured summaries of the logged-in user\'s own API-created games, including the exact stored charge for each game.',
        auth: true,
        bodyFields: [],
        urlParams: [],
        queryParams: [],
        response: {
            status: 'success',
            count: 1,
            games: [
                {
                    roomId: 'ROOM_ID',
                    roomName: 'Rajni Mahato',
                    status: 'created',
                    createdAt: '2026-06-14T12:00:00.000Z',
                    maxPlayers: 2,
                    gameMode: {
                        value: 'classic',
                        label: 'Classic',
                        description: 'Standard Ludo. All 4 tokens must reach home to win.',
                    },
                    features: {
                        voiceEnabled: true,
                        chatEnabled: true,
                        emojiEnabled: true,
                        isEncrypted: true,
                        vsBot: false,
                        botWinRate: null,
                    },
                    winner: null,
                    private: false,
                    players: [
                        { seat: 1, name: 'Rajni Mahato', color: 'red', mobile: '09122592250', email: 'rajnikantmahato33435@gmail.com', ip: null },
                        { seat: 2, name: 'Player 2', color: null, mobile: null, email: null, ip: null },
                    ],
                    urls: {
                        room: `${window.location.origin}/join/ROOM_ID`,
                        view: `${window.location.origin}/watch/ROOM_ID`,
                    },
                    pricing: {
                        currency: 'credits',
                        basePrice: 10,
                        fallbackBasePrice: 10,
                        perPlayerPrice: 0,
                        humanCount: 2,
                        totalPrice: 10,
                        priceSource: 'fallback',
                        priceRole: 'api_manager',
                    },
                    apikey: 'lk_xxxxxxxxxxxxxxxxxxxxxxxxxxxx',
                },
            ],
        },
    },
    {
        id: 'history-all',
        group: 'History',
        name: 'All Game History',
        method: 'GET',
        path: '/api/history/all',
        description: 'Fetch all game history records. Optionally filter by status.',
        auth: true,
        bodyFields: [],
        urlParams: [],
        queryParams: [
            { key: 'status', label: 'Status Filter', type: 'select', options: ['','all','created','playing','completed'], default: '' },
        ],
    },
    {
        id: 'history-by-apikey',
        group: 'History',
        name: 'History by API Key',
        method: 'GET',
        path: '/api/history/by-apikey/:apikey',
        description: 'Fetch game history for a specific API key.',
        auth: true,
        bodyFields: [],
        urlParams: [
            { key: 'apikey', label: 'API Key', type: 'text', required: true, placeholder: 'your-api-key' },
        ],
        queryParams: [
            { key: 'status', label: 'Status Filter', type: 'select', options: ['','all','created','playing','completed'], default: '' },
        ],
    },
    {
        id: 'history-by-room',
        group: 'History',
        name: 'History by Room ID',
        method: 'GET',
        path: '/api/history/by-room/:roomid',
        description: 'Fetch history for a specific room ID.',
        auth: true,
        bodyFields: [],
        urlParams: [
            { key: 'roomid', label: 'Room ID', type: 'text', required: true, placeholder: 'mongo-object-id' },
        ],
        queryParams: [
            { key: 'status', label: 'Status Filter', type: 'select', options: ['','all','created','playing','completed'], default: '' },
        ],
    },
    {
        id: 'history-delete',
        group: 'History',
        name: 'Delete History Record',
        method: 'DELETE',
        path: '/api/history/:id',
        description: 'Permanently delete a single game history record.',
        auth: true,
        bodyFields: [],
        urlParams: [
            { key: 'id', label: 'History Record ID', type: 'text', required: true, placeholder: 'mongo-object-id' },
        ],
        queryParams: [],
    },
    {
        id: 'history-update',
        group: 'History',
        name: 'Update History Record',
        method: 'PUT',
        path: '/api/history/:id',
        description: 'Mark a game as completed or set a winner.',
        auth: true,
        bodyFields: [
            { key: 'completed', label: 'Mark Completed', type: 'select', options: ['true','false'], default: 'true' },
            { key: 'winner',    label: 'Winner Color',   type: 'select', options: ['','red','green','blue','yellow'], default: '' },
        ],
        urlParams: [
            { key: 'id', label: 'History Record ID', type: 'text', required: true, placeholder: 'mongo-object-id' },
        ],
        queryParams: [],
    },
    {
        id: 'rooms-all',
        group: 'Active Rooms',
        name: 'List Active Rooms',
        method: 'GET',
        path: '/api/rooms/all',
        description: 'Paginated list of all active rooms.',
        auth: true,
        bodyFields: [],
        urlParams: [],
        queryParams: [
            { key: 'page',  label: 'Page',  type: 'number', default: '1' },
            { key: 'limit', label: 'Limit', type: 'number', default: '10' },
        ],
    },
    {
        id: 'rooms-get',
        group: 'Active Rooms',
        name: 'Get Room Details',
        method: 'GET',
        path: '/api/rooms/:id',
        description: 'Full details for a specific active room.',
        auth: true,
        bodyFields: [],
        urlParams: [
            { key: 'id', label: 'Room ID', type: 'text', required: true, placeholder: 'mongo-object-id' },
        ],
        queryParams: [],
    },
    {
        id: 'rooms-replay',
        group: 'Active Rooms',
        name: 'Replay Data',
        method: 'GET',
        path: '/api/rooms/:id/replay',
        description: 'Replay payload with move log, chat log, and initial pawns for a room.',
        auth: true,
        bodyFields: [],
        urlParams: [
            { key: 'id', label: 'Room ID', type: 'text', required: true, placeholder: 'mongo-object-id' },
        ],
        queryParams: [],
    },
    {
        id: 'rooms-end',
        group: 'Active Rooms',
        name: 'End Game',
        method: 'PUT',
        path: '/api/rooms/:id/end',
        description: 'Force-end a running game. Optionally set a winner color.',
        auth: true,
        bodyFields: [
            { key: 'winner', label: 'Winner Color', type: 'select', options: ['','red','green','blue','yellow'], default: '' },
        ],
        urlParams: [
            { key: 'id', label: 'Room ID', type: 'text', required: true, placeholder: 'mongo-object-id' },
        ],
        queryParams: [],
    },
    {
        id: 'rooms-features',
        group: 'Active Rooms',
        name: 'Toggle Room Features',
        method: 'PUT',
        path: '/api/rooms/:id/features',
        description: 'Enable or disable voice and video for a room.',
        auth: true,
        bodyFields: [
            { key: 'voiceEnabled', label: 'Voice Enabled', type: 'select', options: ['true','false'], default: 'true' },
            { key: 'videoEnabled', label: 'Video Enabled', type: 'select', options: ['true','false'], default: 'false' },
        ],
        urlParams: [
            { key: 'id', label: 'Room ID', type: 'text', required: true, placeholder: 'mongo-object-id' },
        ],
        queryParams: [],
    },
];

const GROUPS = [...new Set(ENDPOINTS.map(e => e.group))];
const ENDPOINT_PERMISSION_MAP = {
    'create-game': 'createGame',
    'available-games': 'availableGames',
    'list-games': 'listGames',
    'history-all': 'historyAll',
    'history-by-apikey': 'historyByApiKey',
    'history-by-room': 'historyByRoom',
    'history-update': 'historyUpdate',
    'history-delete': 'historyDelete',
    'rooms-all': 'roomsAll',
    'rooms-get': 'roomDetails',
    'rooms-replay': 'roomReplay',
    'rooms-end': 'roomsEnd',
    'rooms-features': 'roomFeatures',
};

// ── helpers ───────────────────────────────────────────────────────────────────
function buildUrl(endpoint, urlVals, queryVals) {
    let url = BACKEND + endpoint.path;
    (endpoint.urlParams || []).forEach(p => {
        url = url.replace(`:${p.key}`, urlVals[p.key] || `:${p.key}`);
    });
    const qp = (endpoint.queryParams || [])
        .filter(p => queryVals[p.key])
        .map(p => `${p.key}=${encodeURIComponent(queryVals[p.key])}`);
    if (qp.length) url += '?' + qp.join('&');
    return url;
}

function buildBodyObj(endpoint, bodyVals, playerVals) {
    const body = {};
    (endpoint.bodyFields || []).forEach(f => {
        if (f.showIf && !f.showIf(bodyVals)) return;
        const v = bodyVals[f.key];
        // _botSetup is a UI-only field — expand it into vsBot + players
        if (f.key === '_botSetup') {
            const combo = BOT_SETUP_MAP[v];
            if (combo) { body.vsBot = combo.vsBot; body.players = combo.players; }
            return;
        }
        if (v !== undefined && v !== '') {
            if (v === 'true') body[f.key] = true;
            else if (v === 'false') body[f.key] = false;
            else if (f.type === 'number' || f.toNumber) body[f.key] = Number(v);
            else body[f.key] = v;
        }
    });
    if (endpoint.hasPerPlayer && playerVals.length) {
        body.playerDetails = playerVals.map(p => ({
            name: p.name || '',
            ...(p.mobile ? { mobile: p.mobile } : {}),
            ...(p.email ? { email: p.email } : {}),
            ...(p.ip ? { ip: p.ip } : {}),
        }));
    }
    return body;
}

function buildCurl(endpoint, urlVals, queryVals, bodyVals, playerVals) {
    const url = buildUrl(endpoint, urlVals, queryVals);
    const headers = [];
    if (['POST', 'PUT'].includes(endpoint.method)) headers.push(`-H "Content-Type: application/json"`);

    const body = buildBodyObj(endpoint, bodyVals, playerVals);
    let curl = `curl -X ${endpoint.method} "${url}"`;
    if (headers.length) curl += ' \\\n  ' + headers.join(' \\\n  ');
    if (['POST', 'PUT'].includes(endpoint.method) && Object.keys(body).length) {
        curl += ` \\\n  -d '${JSON.stringify(body, null, 2).replace(/\n/g, '\n      ')}'`;
    }
    return curl;
}

// ── sub-components ────────────────────────────────────────────────────────────
const MethodBadge = ({ method }) => (
    <span style={{
        padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 800,
        color: '#fff', background: METHOD_COLOR[method] || '#888', letterSpacing: 0.5,
    }}>{method}</span>
);

const AuthBadge = ({ required, adminOnly }) => (
    <span style={{
        padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
        background: required ? 'rgba(255,179,0,0.15)' : 'rgba(0,200,83,0.12)',
        color: required ? C.amber : C.green,
        border: `1px solid ${required ? 'rgba(255,179,0,0.3)' : 'rgba(0,200,83,0.25)'}`,
    }}>{required ? (adminOnly ? '🔐 admin required' : '🔐 login required') : '🌐 public'}</span>
);

const inputSt = {
    width: '100%', boxSizing: 'border-box',
    background: 'rgba(6,11,31,0.7)', border: `1px solid ${C.border}`,
    color: '#fff', borderRadius: 8, padding: '8px 11px', fontSize: 13,
    outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s',
};
const focusIn  = e => { e.target.style.borderColor = C.cyan; e.target.style.boxShadow = `0 0 0 2px rgba(0,212,255,0.1)`; };
const focusOut = e => { e.target.style.borderColor = C.border; e.target.style.boxShadow = 'none'; };

function Field({ def, value, onChange, locked = false, assistText = '' }) {
    if (def.type === 'select') return (
        <div>
            <label style={{ display: 'block', color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 5, fontWeight: 600, letterSpacing: 0.4 }}>
                {def.label}{def.required && <span style={{ color: C.orange }}> *</span>}
            </label>
            <select value={value ?? def.default ?? ''} onChange={e => onChange(e.target.value)}
                disabled={locked}
                style={{ ...inputSt, cursor: 'pointer' }} onFocus={focusIn} onBlur={focusOut}>
                {def.options.map(o => (
                    <option key={o} value={o} style={{ background: '#0d1332' }}>
                        {def.optionLabels?.[o] || o || '(empty)'}
                    </option>
                ))}
            </select>
            {assistText && (
                <div style={{ fontSize: 11, color: 'rgba(0,212,255,0.72)', marginTop: 4 }}>{assistText}</div>
            )}
            {def.descriptions && value && def.descriptions[value] && (
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>ℹ {def.descriptions[value]}</div>
            )}
            {def.note && (
                <div style={{ fontSize: 11, color: 'rgba(255,179,0,0.6)', marginTop: 4 }}>⚡ {def.note}</div>
            )}
        </div>
    );
    if (def.type === 'password') {
        return (
            <div>
                <label style={{ display: 'block', color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 5, fontWeight: 600, letterSpacing: 0.4 }}>
                    {def.label}{def.required && <span style={{ color: C.orange }}> *</span>}
                </label>
                <PasswordField
                    value={value ?? ''}
                    onChange={e => onChange(e.target.value)}
                    placeholder={def.placeholder || ''}
                    readOnly={locked}
                    style={{
                        ...inputSt,
                        opacity: locked ? 0.82 : 1,
                        cursor: locked ? 'not-allowed' : 'text',
                    }}
                    onFocus={focusIn}
                    onBlur={focusOut}
                />
                {assistText && (
                    <div style={{ fontSize: 11, color: 'rgba(0,212,255,0.72)', marginTop: 4 }}>{assistText}</div>
                )}
                {def.note && (
                    <div style={{ fontSize: 11, color: 'rgba(255,179,0,0.6)', marginTop: 4 }}>⚡ {def.note}</div>
                )}
            </div>
        );
    }
    return (
        <div>
            <label style={{ display: 'block', color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 5, fontWeight: 600, letterSpacing: 0.4 }}>
                {def.label}{def.required && <span style={{ color: C.orange }}> *</span>}
            </label>
            <input
                type={def.type === 'password' ? 'password' : def.type === 'number' ? 'number' : 'text'}
                value={value ?? ''}
                onChange={e => onChange(e.target.value)}
                placeholder={def.placeholder || ''}
                readOnly={locked}
                style={{
                    ...inputSt,
                    opacity: locked ? 0.82 : 1,
                    cursor: locked ? 'not-allowed' : 'text',
                }}
                onFocus={focusIn} onBlur={focusOut}
            />
            {assistText && (
                <div style={{ fontSize: 11, color: 'rgba(0,212,255,0.72)', marginTop: 4 }}>{assistText}</div>
            )}
        </div>
    );
}

function Panel({ children, s }) {
    return (
        <div style={{ background: C.card, backdropFilter: 'blur(14px)', border: `1px solid ${C.border}`, borderRadius: 16, overflow: 'hidden', ...s }}>
            {children}
        </div>
    );
}

function SectionLabel({ children }) {
    return (
        <div style={{ fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 14 }}>
            {children}
        </div>
    );
}

// ── main ──────────────────────────────────────────────────────────────────────
export default function ApiDocsPage() {
    const { user, isAdmin, isLoggedIn, loading: authLoading, logout, hasApiPermission } = useAuth();
    const loggedInApiKey = user?.apiKey || '';

    const canAccessEndpoint = useCallback(ep => {
        if (ep.adminOnly && !isAdmin) return false;
        const permissionKey = ENDPOINT_PERMISSION_MAP[ep.id];
        if (!permissionKey) return true;
        return hasApiPermission(permissionKey);
    }, [hasApiPermission, isAdmin]);
    const visibleEndpoints = ENDPOINTS.filter(canAccessEndpoint);
    const [selected, setSelected]     = useState(visibleEndpoints[0] || ENDPOINTS[0]);
    const [urlVals, setUrlVals]       = useState({});
    const [queryVals, setQueryVals]   = useState({});
    const [bodyVals, setBodyVals]     = useState({ players: '4', gameMode: 'classic', _botSetup: 'none', isEncrypted: 'true', viewroom: 'true' });
    const [playerVals, setPlayerVals] = useState([emptyPlayer(), emptyPlayer(), emptyPlayer(), emptyPlayer()]);
    const [response, setResponse]     = useState(null);
    const [resError, setResError]     = useState(null);
    const [loading, setLoading]       = useState(false);
    const [copied, setCopied]         = useState(false);
    const [showWsDocs, setShowWsDocs] = useState(false);

    // Auto-populate API key from logged-in user
    useEffect(() => {
        if (!loggedInApiKey) return;
        setBodyVals(prev => (prev.apikey === loggedInApiKey ? prev : { ...prev, apikey: loggedInApiKey }));
        if (selected?.urlParams?.some(param => param.key === 'apikey')) {
            setUrlVals(prev => (prev.apikey === loggedInApiKey ? prev : { ...prev, apikey: loggedInApiKey }));
        }
    }, [loggedInApiKey, selected]);

    useEffect(() => {
        if (!selected || visibleEndpoints.some(ep => ep.id === selected.id)) return;
        setSelected(visibleEndpoints[0] || ENDPOINTS[0]);
    }, [selected, visibleEndpoints]);

    const botSetupCombo = BOT_SETUP_MAP[bodyVals._botSetup];
    const isBotMode     = !!botSetupCombo;
    const playerCount   = isBotMode ? botSetupCombo.humans : parseInt(bodyVals.players || '4', 10);
    const resolvedUrlVals = selected.urlParams?.some(param => param.key === 'apikey') && loggedInApiKey
        ? { ...urlVals, apikey: loggedInApiKey }
        : urlVals;
    const resolvedBodyVals = selected.bodyFields?.some(field => field.key === 'apikey') && loggedInApiKey
        ? { ...bodyVals, apikey: loggedInApiKey }
        : bodyVals;

    const selectEndpoint = useCallback(ep => {
        if (!canAccessEndpoint(ep)) return;
        setSelected(ep);
        setShowWsDocs(false);
        setResponse(null);
        setResError(null);
        const defs = {};
        (ep.bodyFields || []).forEach(f => { if (f.default !== undefined) defs[f.key] = f.default; });
        if (ep.bodyFields?.some(field => field.key === 'apikey') && loggedInApiKey) defs.apikey = loggedInApiKey;
        setBodyVals(defs);
        const qd = {};
        (ep.queryParams || []).forEach(f => { if (f.default !== undefined) qd[f.key] = f.default; });
        setQueryVals(qd);
        setUrlVals(ep.urlParams?.some(field => field.key === 'apikey') && loggedInApiKey ? { apikey: loggedInApiKey } : {});
        setPlayerVals([emptyPlayer(), emptyPlayer(), emptyPlayer(), emptyPlayer()]);
    }, [canAccessEndpoint, loggedInApiKey]);

    const setBodyVal = (key, val) => {
        setBodyVals(prev => ({ ...prev, [key]: val }));
    };

    const setPlayerField = (idx, field, val) => {
        setPlayerVals(prev => {
            const next = [...prev];
            next[idx] = { ...next[idx], [field]: val };
            return next;
        });
    };

    const curlText    = buildCurl(selected, resolvedUrlVals, queryVals, resolvedBodyVals, playerVals.slice(0, playerCount));
    const resolvedUrl = buildUrl(selected, resolvedUrlVals, queryVals);

    const copy = text => {
        navigator.clipboard.writeText(text).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const sendRequest = async () => {
        if (!canAccessEndpoint(selected)) {
            setResError({ status: 403, message: 'Access denied', data: { message: 'This endpoint requires admin privileges.' } });
            return;
        }
        setLoading(true); setResponse(null); setResError(null);
        try {
            const body = buildBodyObj(selected, resolvedBodyVals, selected.hasPerPlayer ? playerVals.slice(0, playerCount) : []);

            let res;
            const reqCfg = { withCredentials: true };
            if (selected.method === 'GET')    res = await axios.get(resolvedUrl, reqCfg);
            if (selected.method === 'POST')   res = await axios.post(resolvedUrl, body, reqCfg);
            if (selected.method === 'PUT')    res = await axios.put(resolvedUrl, body, reqCfg);
            if (selected.method === 'DELETE') res = await axios.delete(resolvedUrl, reqCfg);

            setResponse({ status: res.status, data: res.data });
        } catch (err) {
            setResError({ status: err.response?.status, message: err.message, data: err.response?.data });
        } finally { setLoading(false); }
    };

    if (authLoading) return null;
    if (!isLoggedIn) return <Navigate to="/auth/login" replace />;

    return (
        <div style={{ minHeight: '100vh', background: `${C.bg} radial-gradient(ellipse 100% 40% at 50% 0%, rgba(0,212,255,0.06) 0%, transparent 60%)`, color: '#fff', display: 'flex', flexDirection: 'column' }}>

            {/* ── top bar ── */}
            <header style={{
                position: 'sticky', top: 0, zIndex: 30,
                background: 'rgba(6,11,31,0.92)', backdropFilter: 'blur(20px)',
                borderBottom: `1px solid ${C.border}`,
                padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 54, gap: 12,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20 }}>🎲</span>
                    <span style={{ fontWeight: 800, fontSize: 15, background: `linear-gradient(90deg,${C.cyan},#fff)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>LudoKing API</span>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontWeight: 500 }}>v1 · Interactive Docs</span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {user ? (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 8, background: 'rgba(0,200,83,0.1)', border: '1px solid rgba(0,200,83,0.2)' }}>
                                <span style={{ fontSize: 13 }}>{user.role === 'admin' ? '⚙️' : '🔑'}</span>
                                <span style={{ fontSize: 12, color: '#a7f3d0', fontWeight: 700 }}>{user.username}</span>
                            </div>
                            {user.apiKey && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
                                    <span>API key auto-set</span>
                                    <span style={{ color: C.green }}>✓</span>
                                </div>
                            )}
                            <button onClick={logout} style={{ padding: '5px 10px', borderRadius: 8, border: `1px solid rgba(255,59,48,0.3)`, background: 'rgba(255,59,48,0.08)', color: '#ff3b30', fontSize: 11, cursor: 'pointer' }}>Sign out</button>
                        </>
                    ) : (
                        <>
                            <Link to="/auth/login" style={{ padding: '5px 12px', borderRadius: 8, border: `1px solid rgba(0,212,255,0.3)`, background: 'rgba(0,212,255,0.08)', color: C.cyan, fontSize: 12, textDecoration: 'none', whiteSpace: 'nowrap' }}>Sign In</Link>
                        </>
                    )}
                    <Link to="/" style={{ padding: '5px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.5)', fontSize: 12, textDecoration: 'none' }}>← Home</Link>
                </div>
            </header>

            <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>

                {/* ── sidebar ── */}
                <aside style={{ width: 230, flexShrink: 0, background: 'rgba(6,11,31,0.6)', borderRight: `1px solid ${C.border}`, overflowY: 'auto', padding: '14px 0' }}>
                    {GROUPS.map(group => {
                        const groupEps = ENDPOINTS.filter(e => e.group === group);
                        const visibleGroupEps = groupEps.filter(e => visibleEndpoints.includes(e));
                        if (visibleGroupEps.length === 0) return null;
                        return (
                        <div key={group} style={{ marginBottom: 6 }}>
                            <div style={{ padding: '5px 14px', fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase', letterSpacing: 1.4 }}>{group}</div>
                            {visibleGroupEps.map(ep => {
                                const active = selected.id === ep.id;
                                return (
                                    <button key={ep.id} onClick={() => selectEndpoint(ep)} style={{
                                        width: '100%', textAlign: 'left', padding: '7px 14px',
                                        background: active ? 'rgba(0,212,255,0.1)' : 'transparent',
                                        borderLeft: active ? `3px solid ${C.cyan}` : '3px solid transparent',
                                        border: 'none', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.15s',
                                    }}>
                                        <MethodBadge method={ep.method} />
                                        <span style={{ fontSize: 12, color: active ? '#fff' : 'rgba(255,255,255,0.5)', fontWeight: active ? 700 : 400, lineHeight: 1.3 }}>{ep.name}</span>
                                    </button>
                                );
                            })}
                        </div>
                        );
                    })}
                    {/* WebSocket docs entry */}
                    <div style={{ margin: '8px 0 0', padding: '0' }}>
                        <div style={{ padding: '5px 14px', fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase', letterSpacing: 1.4 }}>WebSocket</div>
                        <button onClick={() => setShowWsDocs(true)} style={{
                            width: '100%', textAlign: 'left', padding: '7px 14px',
                            background: showWsDocs ? 'rgba(0,212,255,0.1)' : 'transparent',
                            borderLeft: showWsDocs ? `3px solid ${C.cyan}` : '3px solid transparent',
                            border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                            <span style={{ padding: '3px 7px', borderRadius: 5, fontSize: 10, fontWeight: 800, background: 'rgba(0,212,255,0.2)', color: C.cyan }}>WS</span>
                            <span style={{ fontSize: 12, color: showWsDocs ? '#fff' : 'rgba(255,255,255,0.5)', fontWeight: showWsDocs ? 700 : 400 }}>Live Game Data</span>
                        </button>
                    </div>

                    <div style={{ margin: '12px 14px 0', paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                        <div style={{ padding: '5px 0 9px', fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase', letterSpacing: 1.4 }}>Navigation</div>
                        {isAdmin && (
                            <Link to="/admincp/admin" style={{ display: 'block', textDecoration: 'none', color: 'rgba(255,255,255,0.65)', fontSize: 12, fontWeight: 700, padding: '8px 0' }}>
                                ← Admin Panel
                            </Link>
                        )}
                        <Link to="/" style={{ display: 'block', textDecoration: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600, padding: '8px 0' }}>
                            ← Home
                        </Link>
                    </div>
                </aside>

                {/* ── main ── */}
                <main style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

                    {/* ── WebSocket docs view ── */}
                    {showWsDocs && <WsDocsView Panel={Panel} SectionLabel={SectionLabel} C={C} backendUrl={BACKEND} />}

                    {/* endpoint header */}
                    {!showWsDocs && <>
                    <Panel>
                        <div style={{ padding: '18px 22px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                                <MethodBadge method={selected.method} />
                                <code style={{ fontSize: 13, fontWeight: 700, color: C.cyan, background: 'rgba(0,212,255,0.07)', padding: '4px 10px', borderRadius: 7, border: `1px solid rgba(0,212,255,0.15)` }}>{selected.path}</code>
                                <AuthBadge required={selected.auth} adminOnly={selected.adminOnly} />
                            </div>
                            <h1 style={{ margin: '0 0 6px', fontSize: 19, fontWeight: 800 }}>{selected.name}</h1>
                            <p style={{ margin: 0, color: 'rgba(255,255,255,0.5)', fontSize: 13, lineHeight: 1.6 }}>{selected.description}</p>
                            {selected.auth && (
                                <div style={{ marginTop: 12, padding: '9px 13px', borderRadius: 9, background: 'rgba(255,179,0,0.08)', border: '1px solid rgba(255,179,0,0.2)', fontSize: 12, color: 'rgba(255,200,80,0.85)' }}>
                                    🔐 Requires an active <strong>login session</strong> with permission for this endpoint.
                                </div>
                            )}
                        </div>
                    </Panel>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>

                        {/* ── LEFT: form ── */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                            {/* URL params */}
                            {selected.urlParams.length > 0 && (
                                <Panel>
                                    <div style={{ padding: '16px 18px' }}>
                                        <SectionLabel>Path Parameters</SectionLabel>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                                            {selected.urlParams.map(f => (
                                                <Field key={f.key} def={f} value={resolvedUrlVals[f.key]}
                                                    locked={f.key === 'apikey' && !!loggedInApiKey}
                                                    assistText={f.key === 'apikey' && loggedInApiKey ? 'Using the API key from your logged-in account.' : ''}
                                                    onChange={v => setUrlVals(p => ({ ...p, [f.key]: v }))} />
                                            ))}
                                        </div>
                                    </div>
                                </Panel>
                            )}

                            {/* Query params */}
                            {selected.queryParams.length > 0 && (
                                <Panel>
                                    <div style={{ padding: '16px 18px' }}>
                                        <SectionLabel>Query Parameters</SectionLabel>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                                            {selected.queryParams.map(f => (
                                                <Field key={f.key} def={f} value={queryVals[f.key]}
                                                    onChange={v => setQueryVals(p => ({ ...p, [f.key]: v }))} />
                                            ))}
                                        </div>
                                    </div>
                                </Panel>
                            )}

                            {/* Static body fields */}
                            {selected.bodyFields.length > 0 && (
                                <Panel>
                                    <div style={{ padding: '16px 18px' }}>
                                        <SectionLabel>Request Options</SectionLabel>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                                            {selected.bodyFields.map(f => {
                                                if (f.showIf && !f.showIf(bodyVals)) return null;
                                                return (
                                                    <Field key={f.key} def={f} value={resolvedBodyVals[f.key]}
                                                        locked={f.key === 'apikey' && !!loggedInApiKey}
                                                        assistText={f.key === 'apikey' && loggedInApiKey ? 'Using the API key from your logged-in account.' : ''}
                                                        onChange={v => setBodyVal(f.key, v)} />
                                                );
                                            })}
                                        </div>
                                    </div>
                                </Panel>
                            )}

                            {/* ── per-player cards ── */}
                            {selected.hasPerPlayer && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {isBotMode && botSetupCombo && (
                                        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(255,107,0,0.08)', border: '1px solid rgba(255,107,0,0.25)', fontSize: 12, color: 'rgba(255,150,80,0.9)', display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ fontSize: 16 }}>🤖</span>
                                            <div>
                                                <strong>Bot Game — {botSetupCombo.label}.</strong>{' '}
                                                Fill details for <strong>{botSetupCombo.humans} human{botSetupCombo.humans > 1 ? 's' : ''}</strong> below.
                                                {botSetupCombo.vsBot} bot{botSetupCombo.vsBot > 1 ? 's' : ''} fill in automatically when all humans join.
                                            </div>
                                        </div>
                                    )}
                                    {Array.from({ length: playerCount }).map((_, i) => (
                                        <div key={i} style={{
                                            background: `rgba(13,19,50,0.75)`,
                                            backdropFilter: 'blur(14px)',
                                            border: `1px solid ${PLAYER_COLORS[i]}44`,
                                            borderRadius: 14, overflow: 'hidden',
                                            boxShadow: `0 0 14px ${PLAYER_COLORS[i]}18`,
                                        }}>
                                            {/* card header */}
                                            <div style={{
                                                padding: '10px 16px',
                                                background: `${PLAYER_COLORS[i]}18`,
                                                borderBottom: `1px solid ${PLAYER_COLORS[i]}33`,
                                                display: 'flex', alignItems: 'center', gap: 10,
                                            }}>
                                                <span style={{ width: 10, height: 10, borderRadius: '50%', background: PLAYER_COLORS[i], boxShadow: `0 0 6px ${PLAYER_COLORS[i]}` }} />
                                                <span style={{ fontWeight: 800, fontSize: 13, color: PLAYER_COLORS[i] }}>{PLAYER_LABELS[i]}</span>
                                            </div>
                                            {/* fields */}
                                            <div style={{ padding: '14px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                                {PER_PLAYER_FIELDS.map(f => (
                                                    <div key={f.key}>
                                                        <label style={{ display: 'block', color: 'rgba(255,255,255,0.45)', fontSize: 10, marginBottom: 4, fontWeight: 700, letterSpacing: 0.5 }}>
                                                            {f.label.toUpperCase()}{f.key === 'name' ? ' *' : ''}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={playerVals[i]?.[f.key] || ''}
                                                            onChange={e => setPlayerField(i, f.key, e.target.value)}
                                                            placeholder={f.placeholder}
                                                            style={{ ...inputSt, padding: '7px 10px', fontSize: 12 }}
                                                            onFocus={e => { e.target.style.borderColor = PLAYER_COLORS[i]; e.target.style.boxShadow = `0 0 0 2px ${PLAYER_COLORS[i]}22`; }}
                                                            onBlur={focusOut}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* send button */}
                            <button onClick={sendRequest} disabled={loading} style={{
                                padding: '12px', borderRadius: 11, border: 'none', cursor: loading ? 'wait' : 'pointer',
                                background: `linear-gradient(135deg,${C.cyan},#0090e7)`,
                                color: C.bg, fontWeight: 800, fontSize: 14,
                                boxShadow: `0 0 18px ${C.cyan}44`, opacity: loading ? 0.7 : 1, transition: 'all 0.2s',
                            }}>
                                {loading ? '⏳ Sending…' : `▶ Send ${selected.method} Request`}
                            </button>
                        </div>

                        {/* ── RIGHT: curl + response ── */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                            {/* resolved URL */}
                            <Panel>
                                <div style={{ padding: '13px 17px' }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 7 }}>Resolved URL</div>
                                    <div style={{ fontSize: 12, color: C.cyan, fontFamily: 'monospace', wordBreak: 'break-all', lineHeight: 1.6 }}>{resolvedUrl}</div>
                                </div>
                            </Panel>

                            {/* cURL */}
                            <Panel>
                                <div style={{ padding: '11px 17px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1 }}>cURL Command</div>
                                    <button onClick={() => copy(curlText)} style={{
                                        padding: '4px 12px', borderRadius: 7,
                                        border: `1px solid ${copied ? 'rgba(0,200,83,0.4)' : C.border}`,
                                        background: copied ? 'rgba(0,200,83,0.1)' : 'rgba(255,255,255,0.05)',
                                        color: copied ? C.green : 'rgba(255,255,255,0.5)',
                                        fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
                                    }}>{copied ? '✓ Copied!' : 'Copy'}</button>
                                </div>
                                <pre style={{
                                    margin: 0, padding: '14px 17px', fontSize: 11.5, lineHeight: 1.75,
                                    color: '#b4f0ff', fontFamily: 'monospace', background: 'rgba(0,0,0,0.22)',
                                    whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 340, overflowY: 'auto',
                                }}>{curlText}</pre>
                            </Panel>

                            {/* response */}
                            {(response || resError) && (
                                <Panel>
                                    <div style={{ padding: '11px 17px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <div style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1 }}>Response</div>
                                            <span style={{
                                                padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 800,
                                                background: resError ? 'rgba(255,59,48,0.15)' : 'rgba(0,200,83,0.15)',
                                                color: resError ? C.red : C.green,
                                                border: `1px solid ${resError ? 'rgba(255,59,48,0.3)' : 'rgba(0,200,83,0.3)'}`,
                                            }}>{resError ? (resError.status || 'ERROR') : response.status}</span>
                                        </div>
                                        <button onClick={() => copy(JSON.stringify(resError ? resError.data : response.data, null, 2))}
                                            style={{ padding: '4px 12px', borderRadius: 7, border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                            Copy
                                        </button>
                                    </div>
                                    <pre style={{
                                        margin: 0, padding: '14px 17px', fontSize: 11.5, lineHeight: 1.7,
                                        color: resError ? '#ff9999' : '#b4ffcc', fontFamily: 'monospace',
                                        background: 'rgba(0,0,0,0.22)',
                                        maxHeight: 360, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                                    }}>{JSON.stringify(resError ? (resError.data || { error: resError.message }) : response.data, null, 2)}</pre>

                                    {/* per-player join URLs extracted from response */}
                                    {response?.data?.players?.length > 0 && (
                                        <div style={{ padding: '14px 17px', borderTop: `1px solid ${C.border}` }}>
                                            <div style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Player Join URLs</div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                {response.data.players.map((p, i) => (
                                                    <div key={i} style={{ background: `${PLAYER_COLORS[i] || '#888'}11`, border: `1px solid ${PLAYER_COLORS[i] || '#888'}33`, borderRadius: 10, padding: '10px 14px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: PLAYER_COLORS[i] || '#888', flexShrink: 0 }} />
                                                            <span style={{ fontWeight: 700, fontSize: 12, color: PLAYER_COLORS[i] || '#fff' }}>{p.name || `Player ${p.player}`}</span>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                            <input readOnly value={p.join_url}
                                                                onClick={e => e.target.select()}
                                                                style={{ ...inputSt, fontSize: 10, padding: '5px 9px', flex: 1, cursor: 'text', color: C.cyan }} />
                                                            <button onClick={() => copy(p.join_url)}
                                                                style={{ padding: '5px 10px', borderRadius: 7, border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.55)', fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>
                                                                Copy
                                                            </button>
                                                            <button onClick={() => window.open(p.join_url, '_blank')}
                                                                style={{ padding: '5px 10px', borderRadius: 7, border: `1px solid rgba(0,212,255,0.3)`, background: 'rgba(0,212,255,0.08)', color: C.cyan, fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>
                                                                Open
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </Panel>
                            )}

                            {/* example response */}
                            {selected.response && !response && !resError && (
                                <Panel>
                                    <div style={{ padding: '11px 17px', borderBottom: `1px solid ${C.border}` }}>
                                        <div style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: 1 }}>Example Response</div>
                                    </div>
                                    <pre style={{
                                        margin: 0, padding: '14px 17px', fontSize: 11.5, lineHeight: 1.7,
                                        color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace',
                                        background: 'rgba(0,0,0,0.12)', whiteSpace: 'pre-wrap',
                                    }}>{JSON.stringify(selected.response, null, 2)}</pre>
                                </Panel>
                            )}
                        </div>
                    </div>

                    {/* ── game mode reference (create-game only) ── */}
                    {selected.id === 'create-game' && (
                        <Panel>
                            <div style={{ padding: '18px 22px' }}>
                                <SectionLabel>Game Mode Reference</SectionLabel>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(175px,1fr))', gap: 10 }}>
                                    {[
                                        { value: 'classic',  emoji: '🎯', label: 'Classic',         color: '#3b82f6', desc: 'Standard Ludo. All 4 tokens home to win.' },
                                        { value: 'popular',  emoji: '🔥', label: 'Popular ★',       color: '#f97316', desc: 'Classic + 3 🔄 dice re-rolls per player per game.' },
                                        { value: 'quick',    emoji: '⚡', label: 'Quick',            color: '#00d4ff', desc: '2 tokens pre-opened. All 4 home = win. Fast game.' },
                                        { value: 'reverse',  emoji: '🔁', label: 'Reverse (Ulta)',   color: '#f59e0b', desc: 'First to finish all tokens = Loser. Last remaining = Winner.' },
                                        { value: 'token_1',  emoji: '1️⃣', label: '1-Token Win',     color: '#10b981', desc: '2 pre-opened. First 1 token home wins.' },
                                        { value: 'token_2',  emoji: '2️⃣', label: '2-Token Win',     color: '#10b981', desc: '2 pre-opened. First 2 tokens home wins.' },
                                        { value: 'token_3',  emoji: '3️⃣', label: '3-Token Win',     color: '#10b981', desc: '2 pre-opened. First 3 tokens home wins.' },
                                        { value: 'no_cut',   emoji: '✋', label: 'No Dice Cut',     color: '#8b5cf6', desc: '2 pre-opened. Cutting any token = instant loser.' },
                                        { value: 'team_2v2', emoji: '🤝', label: '2 vs 2 Team',     color: '#ec4899', desc: '4 players, 2 teams. Team all 8 home wins. Needs 4 players.' },
                                    ].map(m => (
                                        <div key={m.value} style={{ background: `${m.color}10`, border: `1px solid ${m.color}40`, borderRadius: 11, padding: '13px 15px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                                                <span style={{ fontSize: 17 }}>{m.emoji}</span>
                                                <code style={{ fontSize: 10, fontWeight: 800, color: m.color, background: `${m.color}20`, padding: '2px 7px', borderRadius: 5 }}>{m.value}</code>
                                                {m.value === 'popular' && <span style={{ fontSize: 8, fontWeight: 800, padding: '1px 5px', borderRadius: 10, background: 'linear-gradient(90deg,#f97316,#ef4444)', color: '#fff' }}>POPULAR</span>}
                                            </div>
                                            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 3 }}>{m.label}</div>
                                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>{m.desc}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Panel>
                    )}

                    {/* ── quick start ── */}
                    <Panel>
                        <div style={{ padding: '18px 22px' }}>
                            <SectionLabel>Quick Start</SectionLabel>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, alignItems: 'start' }}>
                                {[
                                    { n: 1, title: 'Multiplayer — create room with player-wise JSON', code: `POST /api/create-game\n{\n  "apikey": "YOUR_KEY",\n  "players": 2,\n  "gameMode": "popular",\n  "openPawnValues": [1, 6],\n  "isEncrypted": true,\n  "viewroom": true,\n  "playerDetails": [\n    {\n      "name": "Rahul",\n      "mobile": "9876543210",\n      "email": "rahul@x.com"\n    },\n    {\n      "name": "Priya",\n      "mobile": "9123456789",\n      "email": "priya@x.com"\n    }\n  ]\n}` },
                                    { n: 2, title: 'Bot game — 1 human vs bots', code: `POST /api/create-game\n{\n  "apikey": "YOUR_KEY",\n  "vsBot": 2,\n  "botWinRate": 90,\n  "gameMode": "classic",\n  "isEncrypted": true,\n  "viewroom": true,\n  "playerDetails": [\n    {\n      "name": "Rahul",\n      "mobile": "9876543210",\n      "email": "rahul@x.com"\n    }\n  ]\n}\n// name is required\n// mobile, email, and ip are optional` },
                                    { n: 3, title: 'Get per-player join URLs from response', code: `players[0].join_url  →  share with Player 1\nplayers[1].join_url  →  share with Player 2\n(bot games only have players[0])\n\nExample URL:\n/join/ROOM_ID?token=abc123&mobile=9876543210\n\nencKey is returned in the API response, not in the URL.\nThe client auto-saves it to localStorage on first join.\nIf viewroom=true, response also includes view_url.` },
                                    { n: 4, title: 'Monitor or end the game', code: `GET /api/rooms/ROOM_ID\nPUT /api/rooms/ROOM_ID/end  { "winner": "red" }` },
                                ].map(s => (
                                    <div key={s.n} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', minWidth: 0, background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 16px' }}>
                                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: `linear-gradient(135deg,${C.cyan},#0060c0)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0, marginTop: 2, color: C.bg }}>
                                            {s.n}
                                        </div>
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 5 }}>{s.title}</div>
                                            <pre style={{ margin: 0, fontSize: 11, color: '#b4f0ff', fontFamily: 'monospace', background: 'rgba(0,0,0,0.22)', padding: '8px 12px', borderRadius: 8, whiteSpace: 'pre-wrap', overflowX: 'auto' }}>{s.code}</pre>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Panel>
                    </>}

                </main>
            </div>
        </div>
    );
}

// ── WebSocket Docs Component ───────────────────────────────────────────────────
const WS_EVENTS = [
    {
        dir: 'emit',
        event: 'game:roll',
        label: 'Roll Dice',
        desc: 'Player requests to roll the dice. Only the player whose turn it is can emit this.',
        payload: `{ seq: <number> }  // optional anti-replay sequence`,
        response: `Server emits "game:roll" to the room with the rolled number (1–6).`,
    },
    {
        dir: 'emit',
        event: 'game:move',
        label: 'Move Pawn',
        desc: 'Move a specific pawn after rolling. Must provide the pawn\'s MongoDB _id.',
        payload: `{ pawnId: "<pawn_id>", seq: <number> }`,
        response: `Server emits "room:data" with updated game state to all room members.`,
    },
    {
        dir: 'on',
        event: 'room:data',
        label: 'Room State Update',
        desc: 'Received whenever the game state changes — pawn moved, player joined, turn changed, etc.',
        payload: null,
        response: `JSON string (parse it):\n{\n  roomId, name, started, winner,\n  players: [{\n    _id, name, color, nowMoving, ready,\n    autoSkipCount, lives\n  }],\n  pawns: [{\n    _id, color, position, basePos\n  }],\n  rolledNumber, gameMode, maxPlayers,\n  openPawnValues, moveSeq,\n  nextMoveTime, vsBot, botColors\n}`,
    },
    {
        dir: 'on',
        event: 'game:roll',
        label: 'Dice Rolled (broadcast)',
        desc: 'Broadcast to all room members when any player rolls the dice.',
        payload: null,
        response: `number  // 1–6`,
    },
    {
        dir: 'on',
        event: 'game:winner',
        label: 'Game Winner',
        desc: 'Emitted to all room members when someone wins.',
        payload: null,
        response: `string  // winning player color: "red" | "blue" | "green" | "yellow"`,
    },
    {
        dir: 'emit',
        event: 'spectate:join',
        label: 'Spectate a Room',
        desc: 'Join as a spectator (no-player watch mode). Use this to observe a game.',
        payload: `{ roomId: "<room_id>" }`,
        response: `Server emits "spectate:joined" then "room:data" for initial state.`,
    },
    {
        dir: 'on',
        event: 'spectate:joined',
        label: 'Spectator Joined',
        desc: 'Confirmation that spectator join succeeded.',
        payload: null,
        response: `{ roomId, name, players, gameMode, ... }`,
    },
    {
        dir: 'emit',
        event: 'gameResults:getAll',
        label: 'Get All Results',
        desc: 'Request list of all completed game results.',
        payload: `(none)`,
        response: `Server emits "gameResults:data" with an array of game result objects.`,
    },
    {
        dir: 'on',
        event: 'gameResults:data',
        label: 'Game Results Data',
        desc: 'Array of completed game result records.',
        payload: null,
        response: `[\n  { _id, roomId, players, winner, date, completed }\n]`,
    },
    {
        dir: 'emit',
        event: 'games:getRunning',
        label: 'Get Running Games',
        desc: 'Request list of currently active/running games.',
        payload: `(none)`,
        response: `Server emits "games:runningData" with active room objects, including stored pricing when available.`,
    },
    {
        dir: 'on',
        event: 'games:runningData',
        label: 'Running Games Data',
        desc: 'Array of currently active game room objects.',
        payload: null,
        response: `[\n  { _id, name, players, gameMode, started, createDate }\n]`,
    },
];

const PAWN_COLORS = [
    { color: 'red',    hex: '#ef4444', desc: 'Player A — seats 0–3' },
    { color: 'blue',   hex: '#3b82f6', desc: 'Player B — seats 4–7' },
    { color: 'green',  hex: '#22c55e', desc: 'Player C — seats 8–11' },
    { color: 'yellow', hex: '#eab308', desc: 'Player D — seats 12–15' },
];

function WsDocsView({ Panel, SectionLabel, C, backendUrl }) {
    return (
        <>
            <Panel>
                <div style={{ padding: '18px 22px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <span style={{ padding: '4px 10px', borderRadius: 7, fontSize: 11, fontWeight: 800, background: 'rgba(0,212,255,0.15)', color: C.cyan, border: '1px solid rgba(0,212,255,0.3)' }}>WS</span>
                        <code style={{ fontSize: 13, fontWeight: 700, color: C.cyan, background: 'rgba(0,212,255,0.07)', padding: '4px 10px', borderRadius: 7, border: '1px solid rgba(0,212,255,0.15)' }}>{backendUrl}</code>
                        <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: 'rgba(0,200,83,0.12)', color: C.green, border: '1px solid rgba(0,200,83,0.25)' }}>🌐 public</span>
                    </div>
                    <h1 style={{ margin: '0 0 6px', fontSize: 19, fontWeight: 800 }}>Ludo Live Data — WebSocket API</h1>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.5)', fontSize: 13, lineHeight: 1.6 }}>
                        LudoKing uses Socket.IO for real-time game events. Connect to the backend URL using the Socket.IO client library.
                        All game state — dice rolls, pawn moves, turn changes, winner — is delivered via these events.
                    </p>
                </div>
            </Panel>

            <Panel>
                <div style={{ padding: '16px 20px' }}>
                    <SectionLabel>Connect</SectionLabel>
                    <pre style={{ margin: 0, fontSize: 12, color: '#b4f0ff', fontFamily: 'monospace', background: 'rgba(0,0,0,0.22)', padding: '14px 16px', borderRadius: 10, whiteSpace: 'pre-wrap' }}>{`import { io } from "socket.io-client";

const socket = io("${backendUrl}", {
  withCredentials: true,          // required for session cookies
  transports: ["websocket", "polling"],
  reconnection: true,
});

socket.on("connect", () => console.log("Connected:", socket.id));`}</pre>
                </div>
            </Panel>

            <Panel>
                <div style={{ padding: '16px 20px' }}>
                    <SectionLabel>Room & Pawn State Structure</SectionLabel>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', marginBottom: 8 }}>Player object in room.players[]</div>
                            <pre style={{ margin: 0, fontSize: 11, color: '#b4f0ff', fontFamily: 'monospace', background: 'rgba(0,0,0,0.22)', padding: '12px 14px', borderRadius: 9, whiteSpace: 'pre-wrap' }}>{`{
  _id:          string,   // MongoDB ID
  name:         string,   // display name
  color:        "red" | "blue"
              | "green" | "yellow",
  nowMoving:    boolean,  // is it their turn?
  ready:        boolean,
  autoSkipCount: number,
  diceRerollsLeft: number // popular mode
}`}</pre>
                        </div>
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', marginBottom: 8 }}>Pawn object in room.pawns[]</div>
                            <pre style={{ margin: 0, fontSize: 11, color: '#b4f0ff', fontFamily: 'monospace', background: 'rgba(0,0,0,0.22)', padding: '12px 14px', borderRadius: 9, whiteSpace: 'pre-wrap' }}>{`{
  _id:     string,    // MongoDB ID (use for game:move)
  color:   string,    // "red" | "blue" | etc.
  basePos: number,    // home base (0–15)
  position: number,   // current board position
  // position === basePos → in base
  // position >= 68     → at home (finished)
  // 0–67              → on board
}`}</pre>
                        </div>
                    </div>
                    <div style={{ marginTop: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', marginBottom: 8 }}>Player colors & pawn indexes</div>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            {PAWN_COLORS.map(p => (
                                <div key={p.color} style={{ padding: '8px 14px', borderRadius: 9, background: `${p.hex}0f`, border: `1px solid ${p.hex}44`, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: p.hex }} />
                                    <span style={{ fontSize: 12, fontWeight: 700, color: p.hex, textTransform: 'capitalize' }}>{p.color}</span>
                                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{p.desc}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </Panel>

            <Panel>
                <div style={{ padding: '16px 20px' }}>
                    <SectionLabel>Events Reference</SectionLabel>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {WS_EVENTS.map((ev, i) => {
                            const isEmit = ev.dir === 'emit';
                            return (
                                <div key={i} style={{ padding: '14px 16px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: `1px solid ${isEmit ? 'rgba(255,107,0,0.2)' : 'rgba(0,212,255,0.18)'}` }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                                        <span style={{
                                            padding: '3px 9px', borderRadius: 7, fontSize: 10, fontWeight: 800,
                                            background: isEmit ? 'rgba(255,107,0,0.18)' : 'rgba(0,212,255,0.15)',
                                            color: isEmit ? '#ff6b00' : C.cyan,
                                            border: `1px solid ${isEmit ? 'rgba(255,107,0,0.35)' : 'rgba(0,212,255,0.3)'}`,
                                        }}>{isEmit ? '↑ EMIT' : '↓ ON'}</span>
                                        <code style={{ fontSize: 13, fontWeight: 700, color: isEmit ? '#ff6b00' : C.cyan, background: 'rgba(0,0,0,0.18)', padding: '3px 9px', borderRadius: 6 }}>"{ev.event}"</code>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>{ev.label}</span>
                                    </div>
                                    <p style={{ margin: '0 0 8px', fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>{ev.desc}</p>
                                    <div style={{ display: 'grid', gridTemplateColumns: ev.payload ? '1fr 1fr' : '1fr', gap: 10 }}>
                                        {ev.payload && (
                                            <div>
                                                <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)', marginBottom: 5 }}>Payload</div>
                                                <pre style={{ margin: 0, fontSize: 11, color: '#ffd080', fontFamily: 'monospace', background: 'rgba(0,0,0,0.22)', padding: '9px 12px', borderRadius: 8, whiteSpace: 'pre-wrap' }}>{ev.payload}</pre>
                                            </div>
                                        )}
                                        <div>
                                            <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)', marginBottom: 5 }}>{ev.payload ? 'Server Response' : 'Payload'}</div>
                                            <pre style={{ margin: 0, fontSize: 11, color: '#b4f0ff', fontFamily: 'monospace', background: 'rgba(0,0,0,0.22)', padding: '9px 12px', borderRadius: 8, whiteSpace: 'pre-wrap' }}>{ev.response}</pre>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Panel>

            <Panel>
                <div style={{ padding: '16px 20px' }}>
                    <SectionLabel>Example: Watch a Game Live</SectionLabel>
                    <pre style={{ margin: 0, fontSize: 12, color: '#b4f0ff', fontFamily: 'monospace', background: 'rgba(0,0,0,0.22)', padding: '14px 16px', borderRadius: 10, whiteSpace: 'pre-wrap' }}>{`// 1. Connect
const socket = io("${backendUrl}", { withCredentials: true });

// 2. Join as spectator
socket.emit("spectate:join", { roomId: "YOUR_ROOM_ID" });

// 3. Listen for state updates
socket.on("room:data", raw => {
  const room = JSON.parse(raw);

  // Who has what dice / position:
  room.players.forEach(p => {
    console.log(p.name, "color:", p.color, "turn:", p.nowMoving);
  });
  room.pawns.forEach(pw => {
    const area = pw.position >= 68   ? "HOME"
               : pw.position === pw.basePos ? "BASE"
               : \`board pos \${pw.position}\`;
    console.log(pw.color, "pawn at", area);
  });
  if (room.rolledNumber) {
    const mover = room.players.find(p => p.nowMoving);
    console.log(mover?.name, "rolled", room.rolledNumber);
  }
});

// 4. Winner event
socket.on("game:winner", color => {
  console.log("Winner:", color);
});`}</pre>
                </div>
            </Panel>
        </>
    );
}
